import type { JSX } from 'react';
import { useStore, type ToolId } from '@/state/store';
import { assignmentColor } from '@/origami/types';

interface ToolDef {
  id: ToolId;
  label: string;
  key: string;
  icon: JSX.Element;
  colorKey?: 'M' | 'V' | 'F' | 'C';
}

const stroke = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.6, strokeLinecap: 'round' as const };

const TOOLS: ToolDef[] = [
  {
    id: 'select',
    label: 'Select',
    key: '1',
    icon: (
      <svg viewBox="0 0 24 24">
        <path d="M5 3l6 16 2.4-6.6L20 10z" {...stroke} strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    id: 'crease',
    label: 'Draw crease (unassigned)',
    key: '2',
    icon: (
      <svg viewBox="0 0 24 24">
        <path d="M4 20L20 4" {...stroke} />
        <circle cx="4" cy="20" r="1.8" {...stroke} />
        <circle cx="20" cy="4" r="1.8" {...stroke} />
      </svg>
    ),
  },
  {
    id: 'mountain',
    label: 'Draw mountain',
    key: '3',
    colorKey: 'M' as const,
    icon: (
      <svg viewBox="0 0 24 24">
        <path d="M3 18l6-9 4 5 3-3 5 7" {...stroke} strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    id: 'valley',
    label: 'Draw valley',
    key: '4',
    colorKey: 'V' as const,
    icon: (
      <svg viewBox="0 0 24 24">
        <path d="M3 6l6 9 4-5 3 3 5-7" {...stroke} strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    id: 'flat',
    label: 'Draw flat crease',
    key: '5',
    colorKey: 'F' as const,
    icon: (
      <svg viewBox="0 0 24 24">
        <path d="M3 12h18" {...stroke} />
      </svg>
    ),
  },
  {
    id: 'cut',
    label: 'Draw cut (kirigami)',
    key: '6',
    colorKey: 'C' as const,
    icon: (
      <svg viewBox="0 0 24 24">
        <path d="M4 4l10 10M20 4L10 14" {...stroke} />
        <circle cx="7" cy="18" r="2.4" {...stroke} />
        <circle cx="17" cy="18" r="2.4" {...stroke} />
      </svg>
    ),
  },
  {
    id: 'move',
    label: 'Move vertex',
    key: '7',
    icon: (
      <svg viewBox="0 0 24 24">
        <path d="M12 3v18M3 12h18M12 3l-2.5 2.5M12 3l2.5 2.5M12 21l-2.5-2.5M12 21l2.5-2.5M3 12l2.5-2.5M3 12l2.5 2.5M21 12l-2.5-2.5M21 12l-2.5 2.5" {...stroke} />
      </svg>
    ),
  },
  {
    id: 'erase',
    label: 'Delete crease',
    key: '8',
    icon: (
      <svg viewBox="0 0 24 24">
        <path d="M6 7h12l-1 13H7L6 7zM9 7V4h6v3" {...stroke} strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    id: 'measure',
    label: 'Measure',
    key: '9',
    icon: (
      <svg viewBox="0 0 24 24">
        <path d="M3 15L15 3l6 6L9 21z" {...stroke} strokeLinejoin="round" />
        <path d="M7 11l2 2M10 8l2 2M13 5l2 2" {...stroke} />
      </svg>
    ),
  },
];

export function Toolbar(): JSX.Element {
  const tool = useStore((s) => s.tool);
  const setTool = useStore((s) => s.setTool);
  const theme = useStore((s) => s.view.theme);
  return (
    <div className="toolbar" role="toolbar" aria-label="Tools">
      {TOOLS.map((t, i) => (
        <div key={t.id} style={{ display: 'contents' }}>
          {i === 1 || i === 6 ? <div className="tool-sep" /> : null}
          <button
            className="tool"
            aria-pressed={tool === t.id}
            data-testid={`tool-${t.id}`}
            title={`${t.label}  (${t.key})`}
            onClick={() => setTool(t.id)}
            style={t.colorKey && tool === t.id ? { color: assignmentColor(t.colorKey, theme) } : undefined}
          >
            {t.icon}
          </button>
        </div>
      ))}
    </div>
  );
}

export const TOOL_KEYS: Record<string, ToolId> = Object.fromEntries(
  TOOLS.map((t) => [t.key, t.id]),
);
